import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { Select } from '@/shared/ui/selects/Select';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiPartiesStatusPatch } from '@/entities/parties';
import { PARTY_STATUS_VALUES, type PartyStatus } from '@/shared/ui/chips/PartyStatusChip';
import type { ApiPartiesGetItem } from '@/shared/types/api/parties';

type Props = {
  party: ApiPartiesGetItem | null;
  onClose: () => void;
};

export const FeaturesPartiesStatusDialog = ({ party, onClose }: Props) => {
  const { t } = useTranslation();
  const STATUS_OPTIONS = useMemo<{ value: PartyStatus; label: string }[]>(
    () => PARTY_STATUS_VALUES.map((value) => ({ value, label: t(`partyStatus.${value}`) })),
    [t],
  );
  const toast = useToast();
  // Default to the party's current status whenever the dialog opens for a new
  // party — useState's initializer only runs once, so without resetting on
  // ``party.id`` changes the select stays blank the second time around.
  const [status, setStatus] = useState<PartyStatus | ''>(party?.status ?? '');
  useEffect(() => {
    if (party) setStatus(party.status);
  }, [party?.id, party?.status]);

  const update = useMutate({
    api: ({ id, s }: { id: string; s: PartyStatus }) => apiPartiesStatusPatch(id, { status: s }),
    invalidateKeys: [queryKeys.parties.all, queryKeys.parcels.all, queryKeys.packages.all],
    onSuccess: () => {
      toast.success(t('parties.toast.statusChanged'));
      onClose();
    },
    onError: (_e, message) => toast.error(message),
  });

  if (!party) return null;

  return (
    <Dialog
      open={Boolean(party)}
      onClose={onClose}
      title={t('parties.status.title')}
      description={t('parties.status.description', { name: party.name })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={update.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!status || status === party.status}
            loading={update.isPending}
            onClick={() => status && update.mutate({ id: party.id, s: status })}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Select
        label={t('parties.status.statusLabel')}
        required
        options={STATUS_OPTIONS}
        value={status}
        placeholder={t('parties.status.statusPlaceholder')}
        onChange={(e) => setStatus(e.target.value as PartyStatus)}
      />
    </Dialog>
  );
};
